import { clearPost, deletePost, editPost } from "app/actions/postingForm";
import { fetchPosts, reportPost } from "app/actions/posts";
import { fetchScheduledPosts, scheduledPosts } from "app/actions/schedule";
import { FilterModel } from "app/models/FilterModel";
import PostModel from "app/models/PostModel";
import { useActions } from "app/utils/hooks";
import { usePagination } from "app/utils/hooks/usePagination";
import { usePermissionChecker } from "app/utils/hooks/usePermission";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { RootState } from "typedefs";

interface QueryParams {
  page?: number;
  team_group_id?: string;
  per_page?: number;
  reply_limit?: number;
}

const INIT_POST_PAGE = 1;
const POSTS_PER_PAGE = 16;
const POSTS_REPLY_LIMIT = 3;

export const TeamsTimelineModel = () => {
  const dispatch = useDispatch();
  const postsActions = useActions({
    fetchPosts,
    fetchScheduledPosts,
    editPost,
    clearPost,
    reportPost,
    deletePost,
  });
  const { selectedTeam } = useSelector(
    (state: RootState) => state.teamGroup,
    shallowEqual,
  );

  const { selectedGroup } = useSelector(
    (state: RootState) => state.groups,
    shallowEqual,
  );

  const { self } = useSelector((state: RootState) => state.user, shallowEqual);

  const { modalInviteIsOpen } = useSelector(
    (state: RootState) => state.teamGroupMemberImport,
    shallowEqual,
  );

  const { scheduledPostsSuccess: scheduledPostsData } = useSelector(
    (state: RootState) => state.schedulePost,
    shallowEqual,
  );

  const [newScehduleModalOpen, setNewScheduleModalOpen] =
    useState<boolean>(false);
  const [scheduledAt, setScheduledAt] = useState(null);
  const [reportModal, setReportModal] = useState({ isOpen: false, post: null });
  const urlParams = useParams<{ teamId: string }>();

  const postsParams = {
    team_group_id: urlParams?.teamId,
    "options[include]": ["replies"],
    page: INIT_POST_PAGE,
    perPage: POSTS_PER_PAGE,
    replyLimit: POSTS_REPLY_LIMIT,
  };

  const [scheduledPostsParams] = useState({
    team_group_id: urlParams?.teamId,
    "options[include]": ["posts"],
    orderBy: "scheduled_at",
    order: "asc",
    timezoneOffset: moment().utcOffset().toString(),
  });

  const query: QueryParams = postsParams;
  const abortControllerRef = useRef<AbortController | null>(null);

  const teamChecker = usePermissionChecker(self?.id, selectedTeam);
  const groupChecker = usePermissionChecker(self?.id, selectedGroup);

  const can = (permission: string) =>
    selectedTeam ? teamChecker.can(permission) : groupChecker.can(permission);

  const { data, pending, sentryRef, updateData, loadInitialData }: any =
    usePagination({
      initialParams: {
        page: INIT_POST_PAGE,
        perPage: POSTS_PER_PAGE,
        ...query,
      },
      fetchData: (params, signal) => postsActions.fetchPosts(params, signal),
      clearDataAction: () => {
        postsActions.clearPost();
      },
      dependencies: [urlParams.teamId],
    });

  useEffect(() => {
    postsActions.fetchScheduledPosts(scheduledPostsParams);
    return () => {
      dispatch(scheduledPosts({ posts: null, error: null, pending: false }));
      // postsActions.clearPost();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      updateData(() => []);
    };
  }, [postsActions]);

  const handleNewSchedulePost = (): void => {
    setNewScheduleModalOpen((prev: boolean): boolean => !prev);
  };

  const onReport = (post: PostModel, message?: string) => {
    postsActions
      .reportPost(post.id, message)
      .then(() => {
        updateData(() =>
          data.filter((filteredPost: PostModel) => filteredPost.id !== post.id),
        );
      })
      .catch((error) => {
        console.error("Error deleting post:", error);
      });
  };

  const onPostReportClick = (post: PostModel) => {
    setReportModal({ isOpen: true, post });
  };

  const reportModalClose = () => {
    setReportModal({ isOpen: false, post: null });
  };

  const onReportHandler = (reason: string) => {
    onReport(reportModal?.post, reason);
    reportModalClose();
  };

  const onPostDelete = (post: PostModel) => {
    const confirmation = window.confirm(
      "Are you sure you want to delete this post?",
    );
    if (confirmation) {
      postsActions
        .deletePost(post.id)
        .then(() => {
          updateData((prevData: PostModel[]) =>
            prevData.filter(
              (filteredPost: PostModel) => filteredPost.id !== post.id,
            ),
          );
        })
        .catch((error) => {
          console.error("Error deleting post:", error);
        });
    }
  };

  const onPostUpdate = (post: PostModel) => {
    if (post) {
      postsActions.editPost(post);
    }
  };

  const onPostPin = () => {
    loadInitialData(postsParams);
  };

  function filterHandler(filters: FilterModel) {
    const filteredQuery = { ...postsParams, ...filters };
    postsActions.clearPost();
    loadInitialData(filteredQuery);
  }

  function onClearFilters() {
    postsActions.clearPost();
    loadInitialData(postsParams);
  }

  const onPost = async (post: PostModel) => {
    if (!post) {
      return;
    }

    if (!post?.scheduledAt) {
      if (!data?.[0]?.pinnedByName) {
        updateData((prevData: PostModel[]) => [
          { ...post, userAvatar: self.avatar },
          ...prevData,
        ]);
      } else if (data?.[0]?.pinnedByName) {
        updateData((prevData: PostModel[]) => {
          const newData = prevData.length > 0 ? [...prevData] : [];
          newData.splice(1, 0, {
            ...post,
            userAvatar: self.avatar,
          });
          return newData;
        });
      }
    } else {
      postsActions.fetchScheduledPosts(scheduledPostsParams);
      setScheduledAt(post.scheduledAt);
      handleNewSchedulePost();
    }
  };

  const canSeePostViews = can("access_views");
  const canSeeWhoLiked = can("access_views");
  const canPost = can("post");
  const canViewMembers = can("view_members");

  // Opens Schedule already filtered to this channel, so its list matches the
  // count on the scheduled posts button.
  const scheduledPostsLocation = `/schedule?target=${urlParams.teamId}`;

  return {
    selectedTeam,
    data,
    scheduledPostsData,
    onPost,
    pending,
    reportModal,
    reportModalClose,
    onReportHandler,
    onPostReportClick,
    onPostDelete,
    onPostUpdate,
    onPostPin,
    sentryRef,
    filterHandler,
    onClearFilters,
    modalInviteIsOpen,
    canSeePostViews,
    canSeeWhoLiked,
    canPost,
    newScehduleModalOpen,
    handleNewSchedulePost,
    scheduledAt,
    canViewMembers,
    scheduledPostsLocation,
  };
};
